import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosInstance';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Share2, ExternalLink } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface MasterPlatform {
    _id: string;
    title: string;
    platformType: 'social-media' | 'audio-streaming';
    image: string;
    baseURL: string;
    isActive: boolean;
}

interface UserPlatform {
    _id: string;
    platformId: { _id: string } | string;
    platformType: 'social-media' | 'audio-streaming';
    url: string;
    handle: string;
    isActive: boolean;
    order: number;
}

interface PlatformEntry {
    platformId: string;
    platformName: string;
    baseURL: string;
    image: string;
    handle: string;
    platformType: 'social-media' | 'audio-streaming';
    userPlatformId?: string;
    originalHandle: string;
    url?: string;
    order: number;
}

interface UserPlatformsManagerProps {
    domain: string;
}

interface SortableCardProps {
    entry: PlatformEntry;
    onHandleChange: (platformId: string, value: string) => void;
    onBlur: (platformId: string) => void;
    savingId: string | null;
}

function SortableCard({ entry, onHandleChange, onBlur, savingId }: SortableCardProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: entry.platformId });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const isConnected = !!entry.userPlatformId;
    const isSaving = savingId === entry.platformId;

    return (
        <Card
            ref={setNodeRef}
            style={style}
            className={`p-3 space-y-2 rounded-lg shadow-none ${
                isConnected
                    ? 'border border-border'
                    : 'border-2 border-dashed border-border'
            }`}
        >
            <div className="flex items-center gap-2">
                {/* Drag handle — only shown for connected platforms */}
                <div
                    className={`shrink-0 touch-none ${isConnected ? 'cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-muted-foreground' : 'invisible pointer-events-none'}`}
                    {...(isConnected ? { ...attributes, ...listeners } : {})}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="9" cy="5" r="1.5" />
                        <circle cx="15" cy="5" r="1.5" />
                        <circle cx="9" cy="12" r="1.5" />
                        <circle cx="15" cy="12" r="1.5" />
                        <circle cx="9" cy="19" r="1.5" />
                        <circle cx="15" cy="19" r="1.5" />
                    </svg>
                </div>
                {/* Platform icon */}
                <div className="w-7 h-7 flex items-center justify-center rounded bg-secondary/50 p-1 shrink-0">
                    <img src={entry.image} alt={entry.platformName} className="w-full h-full object-contain" />
                </div>
                <span className="text-sm font-medium text-foreground flex-1">{entry.platformName}</span>
                {/* Right-side controls */}
                <div className="flex items-center gap-2 shrink-0">
                    {isSaving && <Spinner size="xs" />}
                    {isConnected && !isSaving && entry.url && (
                        <a
                            href={entry.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            title="Visit profile"
                        >
                            <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                    )}
                </div>
            </div>
            {/* Handle input */}
            <Input
                prefix={<span className="font-mono text-xs whitespace-nowrap">{entry.baseURL}</span>}
                value={entry.handle}
                onChange={(e) => onHandleChange(entry.platformId, e.target.value)}
                onBlur={() => onBlur(entry.platformId)}
                className="font-mono"
                placeholder="username"
                disabled={isSaving}
            />
        </Card>
    );
}

const UserPlatformsManager: React.FC<UserPlatformsManagerProps> = ({ domain }) => {
    const [activeTab, setActiveTab] = useState<'social-media' | 'audio-streaming'>('social-media');
    const [entries, setEntries] = useState<PlatformEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    useEffect(() => {
        if (!domain) return;
        setLoading(true);
        Promise.all([
            axios.get(`${BASE_URL}/api/master-platform`),
            axios.get(`${BASE_URL}/api/user-platforms?domain_slug=${domain}&type=social-media`),
            axios.get(`${BASE_URL}/api/user-platforms?domain_slug=${domain}&type=audio-streaming`),
        ])
            .then(([mastersRes, socialRes, audioRes]) => {
                const activeMasters: MasterPlatform[] = (mastersRes.data as MasterPlatform[]).filter((p) => p.isActive);
                const allUserPlatforms: UserPlatform[] = [
                    ...(socialRes.data as UserPlatform[]),
                    ...(audioRes.data as UserPlatform[]),
                ];

                const userMap = new Map<string, UserPlatform>();
                allUserPlatforms.forEach((up) => {
                    const pid = typeof up.platformId === 'object' ? up.platformId._id : (up.platformId as string);
                    userMap.set(pid, up);
                });

                const saved: PlatformEntry[] = [];
                const unsaved: PlatformEntry[] = [];

                activeMasters.forEach((mp) => {
                    const existing = userMap.get(mp._id);
                    if (existing) {
                        saved.push({
                            platformId: mp._id,
                            platformName: mp.title,
                            baseURL: mp.baseURL,
                            image: mp.image,
                            handle: existing.handle,
                            platformType: mp.platformType,
                            userPlatformId: existing._id,
                            originalHandle: existing.handle,
                            url: existing.url,
                            order: existing.order,
                        });
                    } else {
                        unsaved.push({
                            platformId: mp._id,
                            platformName: mp.title,
                            baseURL: mp.baseURL,
                            image: mp.image,
                            handle: '',
                            platformType: mp.platformType,
                            userPlatformId: undefined,
                            originalHandle: '',
                            order: 9999,
                        });
                    }
                });

                saved.sort((a, b) => a.order - b.order);
                setEntries([...saved, ...unsaved]);
            })
            .catch(() => toast.error('Failed to load platforms'))
            .finally(() => setLoading(false));
    }, [domain]);

    const extractHandle = (entry: PlatformEntry, value: string): string => {
        const trimmed = value.trim();
        // Only try to extract if it looks like a full URL
        if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) return trimmed;

        try {
            const pasted = new URL(trimmed);
            const base = new URL(entry.baseURL.endsWith('/') ? entry.baseURL : entry.baseURL + '/');

            // Hosts must match (ignoring www. prefix)
            const normalizeHost = (h: string) => h.replace(/^www\./, '');
            if (normalizeHost(pasted.hostname) !== normalizeHost(base.hostname)) return trimmed;

            // Normalize locale segments (/us/, /in/, /gb/, etc.) so Apple Music and similar
            // services extract the handle correctly regardless of which region URL is pasted.
            const localeSegRe = /^\/[a-z]{2}(-[a-zA-Z]{2})?\//;
            const normalizedPath = pasted.pathname.replace(localeSegRe, () => {
                const baseMatch = base.pathname.match(localeSegRe);
                return baseMatch ? baseMatch[0] : '/';
            });

            // Strip the base path prefix, remove leading slash, strip query/hash
            const basePath = base.pathname;
            let handle = normalizedPath.startsWith(basePath)
                ? normalizedPath.slice(basePath.length)
                : normalizedPath.replace(/^\//, '');

            // Remove trailing slash
            handle = handle.replace(/\/$/, '');

            return handle || trimmed;
        } catch {
            return trimmed;
        }
    };

    const updateHandle = (platformId: string, value: string) => {
        setEntries((prev) =>
            prev.map((e) => {
                if (e.platformId !== platformId) return e;
                const handle = extractHandle(e, value);
                return { ...e, handle };
            })
        );
    };

    const handleBlur = async (platformId: string) => {
        const entry = entries.find((e) => e.platformId === platformId);
        if (!entry) return;
        const trimmed = entry.handle.trim();
        const originalTrimmed = entry.originalHandle.trim();
        if (trimmed === originalTrimmed) return;

        setSavingId(platformId);
        try {
            if (trimmed && !entry.userPlatformId) {
                // New — POST
                const res = await axios.post(`${BASE_URL}/api/user-platforms`, {
                    platformId: entry.platformId,
                    handle: trimmed,
                    domain_slug: domain,
                    platformType: entry.platformType,
                });
                setEntries((prev) =>
                    prev.map((e) =>
                        e.platformId === platformId
                            ? {
                                  ...e,
                                  handle: trimmed,
                                  userPlatformId: res.data._id,
                                  originalHandle: trimmed,
                                  url: res.data.url,
                                  order: res.data.order,
                              }
                            : e
                    )
                );
                toast.success('Platform added!');
            } else if (trimmed && entry.userPlatformId) {
                // Changed — PUT
                const res = await axios.put(`${BASE_URL}/api/user-platforms/${entry.userPlatformId}`, {
                    handle: trimmed,
                });
                setEntries((prev) =>
                    prev.map((e) =>
                        e.platformId === platformId
                            ? { ...e, handle: trimmed, originalHandle: trimmed, url: res.data.url }
                            : e
                    )
                );
                toast.success('Platform updated!');
            } else if (!trimmed && entry.userPlatformId) {
                // Cleared — DELETE
                await axios.delete(`${BASE_URL}/api/user-platforms/${entry.userPlatformId}`);
                setEntries((prev) =>
                    prev.map((e) =>
                        e.platformId === platformId
                            ? { ...e, handle: '', userPlatformId: undefined, originalHandle: '', url: undefined, order: 9999 }
                            : e
                    )
                );
                toast.success('Platform removed!');
            }
        } catch (err: any) {
            setEntries((prev) =>
                prev.map((e) => (e.platformId === platformId ? { ...e, handle: entry.originalHandle } : e))
            );
            toast.error(err.response?.data?.message || 'Failed to save platform');
        } finally {
            setSavingId(null);
        }
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        // Only connected platforms participate in ordering — unconnected ones sit
        // below the sortable list and cannot be persistently reordered.
        const tabConnected = entries.filter(
            (e) => e.platformType === activeTab && !!e.userPlatformId,
        );
        const oldIdx = tabConnected.findIndex((e) => e.platformId === active.id);
        const newIdx = tabConnected.findIndex((e) => e.platformId === over.id);
        if (oldIdx === -1 || newIdx === -1) return;

        const reorderedConnected = arrayMove(tabConnected, oldIdx, newIdx);

        // Merge reordered connected entries back into full list, leaving
        // unconnected entries in their original positions.
        const newEntries = [...entries];
        let ri = 0;
        for (let i = 0; i < newEntries.length; i++) {
            if (newEntries[i].platformType === activeTab && !!newEntries[i].userPlatformId) {
                newEntries[i] = reorderedConnected[ri++];
            }
        }
        setEntries(newEntries);

        const movedEntry = reorderedConnected[newIdx];
        try {
            await axios.put(`${BASE_URL}/api/user-platforms/${movedEntry.userPlatformId}/reorder`, {
                newOrder: newIdx,
            });
            toast.success('Platform reordered!');
        } catch {
            toast.error('Failed to reorder platform');
            setEntries(entries);
        }
    };

    const renderTabContent = (type: 'social-media' | 'audio-streaming') => {
        const filtered = entries.filter((e) => e.platformType === type);

        if (loading) {
            return (
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-18 bg-muted rounded-lg animate-pulse" />
                    ))}
                </div>
            );
        }

        if (filtered.length === 0) {
            return (
                <div className="empty-state">
                    <div className="empty-state-icon">
                        <Share2 className="w-full h-full" />
                    </div>
                    <h3 className="empty-state-title">No platforms available</h3>
                    <p className="empty-state-description">No platforms configured for this category</p>
                </div>
            );
        }

        const connected = filtered.filter((e) => !!e.userPlatformId);
        const unconnected = filtered.filter((e) => !e.userPlatformId);

        return (
            <div className="space-y-3">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={connected.map((e) => e.platformId)} strategy={verticalListSortingStrategy}>
                        {connected.map((entry) => (
                            <SortableCard
                                key={entry.platformId}
                                entry={entry}
                                onHandleChange={updateHandle}
                                onBlur={handleBlur}
                                savingId={savingId}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
                {unconnected.map((entry) => (
                    <SortableCard
                        key={entry.platformId}
                        entry={entry}
                        onHandleChange={updateHandle}
                        onBlur={handleBlur}
                        savingId={savingId}
                    />
                ))}
            </div>
        );
    };

    return (
        <div>
            <div className="mb-4">
                <h2 className="page-title">Platforms</h2>
            </div>
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'social-media' | 'audio-streaming')}>
                <TabsList>
                    <TabsTrigger value="social-media">Social Media</TabsTrigger>
                    <TabsTrigger value="audio-streaming">Audio Streaming</TabsTrigger>
                </TabsList>
                <TabsContent value="social-media" className="mt-4">
                    {renderTabContent('social-media')}
                </TabsContent>
                <TabsContent value="audio-streaming" className="mt-4">
                    {renderTabContent('audio-streaming')}
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default UserPlatformsManager;
